import React, { useCallback, useRef, useState } from 'react';
import cn from 'classnames';
import { useTheme } from '@creditflowresearch/next-themes/src';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Menu from '@creditflowresearch/uilib/Menu';
import { MenuItem } from '@creditflowresearch/uilib/Menu';
import IconTheme from '@creditflowresearch/uilib/Icon/IconTheme';
import IconCheck from '@creditflowresearch/uilib/Icon/IconCheck';
import ThemeToggleOnboarding from './ThemeToggleOnboarding';

import styles from './themeToggle.module.scss';
import IconButton from '@creditflowresearch/uilib/IconButton';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light Mode' },
  { value: 'dark', label: 'Dark Mode' },
  { value: 'system', label: 'Device Default' },
] as const;

const THEME_LABELS: Record<string, string> = Object.fromEntries(
  THEME_OPTIONS.map(({ value, label }) => [value, label]),
);

interface IThemeToggleProps {
  isCompact?: boolean,
}

const ThemeToggle = ({ isCompact }: IThemeToggleProps): React.ReactElement => {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isOnboardingDismissed, setIsOnboardingDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const dismissOnboarding = useCallback((): void => {
    setIsOnboardingDismissed(true);
  }, []);

  const handleOpen = (open: boolean): void => {
    setIsOpen(open);
    if (open) {
      dismissOnboarding();
    }
  };

  const handleSelect = (value: string): void => {
    setTheme(value);
    setIsOpen(false);
  };

  return (
    <div className={ styles.container } ref={ containerRef }>
      <Menu
        trigger={ ({ referenceProps }) => (
          <Tooltip content={ THEME_LABELS[theme] || THEME_LABELS.light } placement="bottom">
            <IconButton
              icon={ <IconTheme className={ styles.themeIcon } /> }
              { ...referenceProps }
              className={ cn(styles.trigger, { [styles.triggerSmall]: isCompact }) }
              ariaLabel="Toggle theme"
              dataTest="themeToggleButton"
            />
          </Tooltip>
        ) }
        open={ isOpen }
        setOpen={ handleOpen }
        placement="bottom-end"
        usePortal={ false }
        contentClassName={ styles.menu }
        dataTest="themeToggleMenu"
        returnFocusOnClose
        initialFocus="firstItem"
      >
        { THEME_OPTIONS.map(({ value, label }, index) => (
          <React.Fragment key={ value }>
            { index === THEME_OPTIONS.length - 1 && <hr className={ styles.divider } /> }
            <MenuItem
              dataTest={ `themeOption-${ value }` }
              onClick={ () => handleSelect(value) }
              className={ cn(styles.menuItem, { [styles.menuItemActive]: theme === value }) }
              textClassName={ styles.menuItemText }
            >
              <span className={ styles.menuItemLabel }>{ label }</span>
              { theme === value && <IconCheck className={ styles.checkIcon } /> }
            </MenuItem>
          </React.Fragment>
        )) }
      </Menu>
      { !isOpen && !isOnboardingDismissed && (
        <ThemeToggleOnboarding onDismiss={ dismissOnboarding } />
      ) }
    </div>
  );
};

export default ThemeToggle;
